import React, { KeyboardEvent, forwardRef, useState } from 'react';
import { X } from 'lucide-react';

interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  tagClassName?: string;
  renderTag?: (tag: string) => React.ReactNode;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onInputKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export const TagInput = forwardRef<HTMLInputElement, TagInputProps>(
  (
    {
      tags = [],
      onTagsChange,
      placeholder = 'Type and press Enter',
      icon,
      tagClassName = 'bg-primary-50 text-primary-700',
      renderTag,
      inputValue: externalInputValue,
      onInputChange,
      onInputKeyDown
    },
    ref
  ) => {
    const isControlled = typeof externalInputValue === 'string' && typeof onInputChange === 'function';
    const [internalInputValue, setInternalInputValue] = useState('');
    const value = isControlled ? externalInputValue : internalInputValue;

    const handleInputChange = (newValue: string) => {
      if (isControlled) {
        onInputChange?.(newValue);
      } else {
        setInternalInputValue(newValue);
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (onInputKeyDown) {
        onInputKeyDown(e);
      }
    };

    const removeTag = (tagToRemove: string) => {
      onTagsChange(tags.filter(tag => tag !== tagToRemove));
    };

    return (
      <div className="space-y-2">
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <div className="flex flex-wrap gap-2">
          {Array.isArray(tags) && tags.map((tag, index) => (
            <span
              key={index}
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${tagClassName}`}
            >
              {renderTag ? renderTag(tag) : <>{icon}{tag}</>}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-1 hover:text-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
    );
  }
);

TagInput.displayName = 'TagInput';